import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Row,
  Col,
  Spinner,
} from 'reactstrap';
import { useContext, useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import purgeRememberedCredentials from '../../../helpers/purgeRememberedCredentials';
import {
  getBiometricLabel,
  getBiometricStatus,
  getBiometricUnavailableHint,
  isBiometricEnabled,
} from '../../../helpers/biometricAuth';

const Login = () => {
  const { handleLogin, handleBiometricLogin, enableBiometricLogin, getProfile } = useContext(AuthContext);
  const recaptchaRef = useRef<any>();
  const autoPromptDone = useRef(false);

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [enrollBiometric, setEnrollBiometric] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState('biometrics');
  const [biometricHint, setBiometricHint] = useState('');

  useEffect(() => {
    let mounted = true;

    purgeRememberedCredentials();

    Promise.all([getBiometricStatus(), isBiometricEnabled()]).then(([status, enabled]) => {
      if (!mounted) {
        return;
      }
      setBiometricAvailable(status.available);
      setBiometricLabel(getBiometricLabel(status.type));
      setBiometricHint(getBiometricUnavailableHint(status));
      setBiometricEnabled(enabled);
      if (enabled && !autoPromptDone.current) {
        autoPromptDone.current = true;
        signInWithBiometrics();
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const onChangeRecaptcha = (value: any) => {
    setCaptcha(value);
  };

  const signInWithBiometrics = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    const result = await handleBiometricLogin();
    if (result?.cancelled) {
      setLoading(false);
      return;
    }
    if (result?.error) {
      toast.error(result?.error);
      if (result?.disabled) {
        setBiometricEnabled(false);
      }
      setLoading(false);
      return;
    }
    getProfile();
    setLoading(false);
  };

  const submit = async (e: any) => {
    e?.preventDefault();
    if (loading || !captcha) {
      return;
    }

    setLoading(true);
    localStorage?.clear();

    const result = await handleLogin(email, password, captcha);
    if (result?.error) {
      toast.error(result?.error);
      setLoading(false);
      recaptchaRef?.current?.reset();
      return;
    }
    if (enrollBiometric) {
      await enableBiometricLogin(email);
    }
    getProfile();
    setLoading(false);
  };

  return (
    <>
      <Col lg="5" md="7">
        <Card className="bg-secondary shadow border-0">
          <CardBody className="px-lg-5 py-lg-5">
            {biometricEnabled && (
              <>
                <div className="text-center mb-3">
                  <Button color="primary" type="button" block={true} disabled={loading} onClick={signInWithBiometrics}>
                    <i className="fas fa-fingerprint mr-2" />
                    {`Sign in with ${biometricLabel}`}
                  </Button>
                </div>
                <div className="text-center text-muted mb-4">
                  <small>or sign in with your password</small>
                </div>
              </>
            )}
            {!biometricEnabled && (
              <div className="text-center text-muted mb-4">
                <small>Sign in with credentials</small>
              </div>
            )}
            <Form role="form" onSubmit={submit}>
              <FormGroup className="mb-3">
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-email-83" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Username/Email"
                    type="text"
                    id="loginEmail"
                    name="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e: any) => setEmail(e?.target?.value)}
                  />
                </InputGroup>
              </FormGroup>
              <FormGroup>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-lock-circle-open" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Password"
                    type="password"
                    id="loginPassword"
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e: any) => setPassword(e?.target?.value)}
                  />
                </InputGroup>
              </FormGroup>
              {biometricAvailable && !biometricEnabled && (
                <FormGroup>
                  <label
                    htmlFor="enrollBiometric"
                    className="text-muted mb-0"
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <input
                      id="enrollBiometric"
                      type="checkbox"
                      checked={enrollBiometric}
                      onChange={(e: any) => setEnrollBiometric(Boolean(e?.target?.checked))}
                      style={{ width: '16px', height: '16px', margin: '0 8px 0 0', cursor: 'pointer' }}
                    />
                    {`Use ${biometricLabel} next time`}
                  </label>
                </FormGroup>
              )}
              {!biometricAvailable && !biometricEnabled && biometricHint && (
                <FormGroup>
                  <small className="text-muted">{biometricHint}</small>
                </FormGroup>
              )}
              <FormGroup>
                <div style={{ width: '100%', display: 'flex', placeContent: 'center' }}>
                  <ReCAPTCHA
                    sitekey="6LcilxsqAAAAACQ2Cv3r9wjjcYLb7db_oHIl4ln0"
                    onChange={onChangeRecaptcha}
                    ref={recaptchaRef}
                  />
                </div>
              </FormGroup>
              <div className="text-center">
                <Button color="primary" type="submit" disabled={loading}>
                  {loading ? <Spinner size="sm">Loading...</Spinner> : 'Sign in'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
        <Row className="mt-3">
          <Col xs="6">
            <a className="text-light" href="/forgot-password">
              <small>Forgot password?</small>
            </a>
          </Col>
          {/*<Col className="text-right" xs="6">
            <a className="text-light" href="/register">
              <small>Create new account</small>
            </a>
            </Col>*/}
        </Row>
      </Col>
    </>
  );
};

export default Login;
